/* Серверный режим экрана «Сменить голос в записи» / «Почистить запись» на движке шаблона (engine-kit, план движков §5.4, §6).
   createVoice(createVoiceServer) (features/voice/engine.ts) отдаёт сюда крючки экрана, а каждая вставка SV спрашивает этот объект:
   цена, готовность, голос, файл, создание, хвост и итог берутся только из сервиса заказа кадра (OrderService привязки EngineFrame).
   Правила:
   - одна привязка — один кадр ServerStudio (ключ кадра и его сервис заказа): у другого кадра — и у нового сервиса под прежним ключом —
     своё состояние экрана (S), свой мост загрузки и свой хвост BotRun (фасад TAIL пересоздаёт BotRun при смене кадра), поэтому запись,
     согласие и заказ одного приложения или владельца не видны и не уходят под другим;
   - движок не пишет в sessionStorage и не зовёт создание сам: заявку, ключ, отправку, опрос и отказы ведёт сервис (run-driver.ts);
     при открытии ничего не отправляется, только показ (подсмотренная заявка — до проверки владельца сервисом);
   - суммы на странице заказа — только из заказа (bot-run-server: receipt, failText); цена на кнопке — для показа, в заказ уходит
     цена сервиса (quote, studioExpectedPrice или скидка формы);
   - файл записи — мост загрузки (upload-bridge.ts): проверки поля формы, лист согласия, «Продолжить загрузку» тем же id;
     запись из повтора — превью сервера (не вышло — загрузить заново, прежний id не используется: решение владельца Q7);
   - готовый итог стоит, пока человек сам не нажмёт «Изменить» (Q8); этапы — локальные этапы шаблона на текущем этапе сервера (Q1);
     отказ до создания — страница «Заказ не создан» с «К настройкам» (Q2); скидка — только если форма её публикует (Q5);
   - тексты (шаг 7): движок берёт словарь профиля в момент показа (t, tf), ядро — через ui.tx и o.tx рамки (текстовый шов BotCore.tx, Q3);
     смена языка (steos:locale) рисует открытый экран заново: хвост — TAIL.show, форма — paintPage; этапы заказа получают подписи нового языка. */
import { endpoints } from '../../api';
import type { Voice } from '../../api/types';
import { voiceOrb } from '../../bridge/voice-orb';
import { uiFormat, uiText } from '../../i18n';
import { platform } from '../../platform';
import { catalogTotal } from '../../bridge/catalog-total';
import { circleBalance, forgetCircleBalance, markSeen, rememberCircleBalance } from '../studio/circle/tool-helpers';
import { tailCurrent, tailSteps } from '../studio/circle/order-tail-model';
import { studioCheapOffer, studioUnitPrice, type StudioApp, type StudioForm } from '../studio/studio-api';
import { failText, receipt, serverFoot, type BotRunApi, type BotRunRun } from '../studio/engine-kit/bot-run-server';
import { openConsentSheet, type ConsentSheet } from '../studio/engine-kit/consent-sheet';
import { ENGINES, type EngineBinding, type EngineBound, type EngineScreen } from '../studio/engine-kit/engine-host';
import { uploadFailureText, type Intent, type OrderService } from '../studio/engine-kit/order-service';
import { createRunDriver, peekIntent, type RunDriver } from '../studio/engine-kit/run-driver';
import { poorBalance, serverUi, type ServerUi } from '../studio/engine-kit/server-ui';
import { createUploadBridge, type AudioLimits, type EngineFile, type UploadBridge } from '../studio/engine-kit/upload-bridge';
import { createVoicePick, createVoiceSample, type VoicePick, type VoiceSample } from '../studio/engine-kit/voice-pick';
import { createVoiceDelivery, type VoiceDelivery } from './voice-delivery';
import { transferVoiceResult } from './voice-transfer';
import {
  DEFAULT_MAX_MS,
  WAVE_N,
  actLabel,
  audioField,
  audioLimits,
  denoiseDefault,
  denoiseField,
  filePeaks,
  isOn,
  modeField,
  onValue,
  perMinute,
  sentLabel,
  voiceField,
  voiceMode,
  voiceOut,
  voicePrice,
  voiceResult,
  voiceTexts,
  type VoiceMode,
} from './voice-model';

/* ---------- контракт с движком (features/voice/engine.ts, make({...})) ---------- */

type Out = 'vm' | 'file';
/** Состояние экрана шаблона (S): режим, голос, запись, «Убрать шум» по режиму, главный способ отправки итога, скидка, звучащий пример */
export interface VoiceState {
  mode: VoiceMode;
  voice: unknown;
  file: EngineFile | null;
  nz: { keep: boolean; other: boolean };
  out: Out;
  cheap: boolean;
  playing: unknown;
  menu: boolean;
}
/** Настройки страницы (BotCore.uiDefaults и init каркаса): то, что читает SV */
export interface EngineUi {
  buzz(kind?: string): void;
  tip(el: Element | null, text: string): void;
  voices(): unknown[];
  myVoices(): unknown[];
  [key: string]: unknown;
}
/** Рамка загрузки BotCore.upload экрана (UP) */
interface EngineUpload {
  o: { mb?: number; [key: string]: unknown };
  say(text: string): void;
  paint(): boolean;
}
/** Подвал BotCore.foot экрана (FOOT) */
interface EngineFoot {
  poor(): boolean;
  poorOpen(): void;
  sync(): void;
}
/** Крючки, которые движок отдаёт make (engine.ts:94-102): tail, up и foot — геттеры (объявлены в движке позже make) */
export interface VoiceEngineHooks {
  S: VoiceState;
  REC: { st: 'idle' | 'asking' | 'rec'; sec: number; sim: boolean; inst: unknown; n: number };
  RP: { on: boolean; pos: number; t0: number; raf: number };
  readonly tail: BotRunApi;
  readonly up: EngineUpload;
  readonly foot: EngineFoot;
  root(): HTMLElement | null;
  mounted(): boolean;
  ui(): EngineUi;
  ic(name: string): string;
  esc(value: string): string;
  setMax(sec: number): void;
  setup(o: unknown): void;
  paintPage(): void;
  paintSrc(): void;
  paintVp(): void;
  sync(): void;
  rpPaint(full?: boolean): void;
  put(f: EngineFile): void;
  setFile(f: EngineFile | null): void;
  liveStop(): void;
  mainOf(r: BotRunRun): Out;
  altOf(r: BotRunRun): Out;
  sendView(r: BotRunRun, k: Out, main: boolean): [icon: string, label: string];
}
/** Голос в виде шаблона (пилл, снимок заказа): шар — строка CSS, is_s2s — строго с сервера */
export interface EngineVoice {
  id: number;
  name: string;
  orb: string;
  tags: string[];
  mine: boolean;
  is_s2s: boolean;
  preview_url: string | null;
}
/** Снимок входа заказа (r.snap BotRun): из S, тела заявки и результата заказа; без записи в памяти — тоже */
export interface VoiceSnap {
  [key: string]: unknown;
  file: EngineFile | null;
  dur: number | null;
  mode: VoiceMode;
  voice: number | undefined;
  vname: string;
  orb: string;
  noise: boolean;
  out: Out;
  cheap: boolean;
  peaks: number[];
}
type Missing = '' | 'load' | 'off' | 'blocked' | 'unavailable';
type Ready = '' | 'upload' | 'upfail' | 'price';

export interface VoiceServer {
  ui<U extends object>(base: U): ServerUi<U>;
  entry(o: unknown): { mode?: VoiceMode; out?: Out; noise?: boolean; voice?: number };
  open(o: object): void;
  close(): void;
  tail(create: (o: Record<string, unknown>) => BotRunApi, opts: Record<string, unknown>): BotRunApi;
  wrap<T>(slot: 'cheap' | 'vsub', value: T, extra?: unknown): T;
  has(field: 'denoise' | 'voice'): boolean;
  missing(): Missing | null;
  ready(): Ready;
  missText(kind: string): string;
  label(): string | null;
  perMinute(): number;
  price(cheap: boolean, durSec: number | null | undefined): number;
  unit(): string;
  note(): string;
  upOpts(): { accept: string; tx(template: string, ...values: unknown[]): string; onPick(files: File[]): void };
  voice(id: unknown): EngineVoice | null;
  voiceTotal(): number;
  pickVoice(id: number): void;
  sample(id: unknown): void;
  stopSample(): void;
  noise(on: boolean): void;
  remove(): void;
  canRecord(): boolean;
  micText(kind: 'off' | 'denied'): string;
  recorded(x: { sec: number; blob: Blob | null; url: string | null }): void;
  start(btn: HTMLElement): void;
  steps(): string[][];
  snap(): VoiceSnap;
  sentPrice(): number;
  drive(r: BotRunRun): void;
  failText(r: BotRunRun): string | null;
  receipt(r: BotRunRun): string;
  click(d: DOMStringMap, el: HTMLElement, r: BotRunRun | null): boolean;
  leave(go: () => void, replace: boolean): void;
  play(r: BotRunRun, at?: number): boolean;
  audioStop(rewind: boolean): void;
  download(): Promise<{ data: Blob; name: string }>;
  /** Как зовёт движок (engine.ts:316): send(кнопка, способ, главная) */
  send(btn: HTMLElement, k: Out, main: boolean): Promise<void>;
  openChat(btn: HTMLElement, k: Out, main: boolean): boolean;
  transfer(btn: HTMLElement, use: 'audio' | 'talk'): void;
}

/* ---------- общие части ---------- */

interface Core {
  html: { unit(n: number, rest: string): string };
  wave: { N: number; html(peaks: number[]): string };
  switchSet(el: Element, on: boolean): void;
}
const core = () => window.BotCore as unknown as Core;
/** «Занята» кнопка шаблона (SteosUI.busy): крутилка, пока идёт действие; уже занята — действие не начинается */
function busyButton<T>(btn: HTMLElement | null, fn: () => Promise<T>): Promise<T | undefined> {
  const busy = window.SteosUI?.busy;
  return btn && busy ? busy(btn, fn) : fn();
}
/** accept рамки BotCore (её проверка типа — до моста): любой звук и расширения, у которых браузер не называет тип (.m4a);
    точные типы и предел — у моста по политике поля формы */
const ACCEPT = 'audio/*,.m4a,.mp3,.wav,.ogg,.oga,.opus,.webm,.aac,.flac';
const TRANSPORT = uploadFailureText(null);
const FORM_SAY = [
  'Некоторые файлы повтора больше недоступны. Загрузите их заново.',
  'Этот инструмент сейчас недоступен. Попробуйте обновить форму.',
  'Инструмент обновляется. Обновите форму позже.',
];
const MODES: readonly VoiceMode[] = ['other', 'keep'];
const num = (value: unknown) => {
  const n = Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isSafeInteger(n) && n > 0 ? n : undefined;
};
const flatWave = () => Array<number>(core().wave?.N ?? WAVE_N).fill(0.07);
/** Этапы шаблона (runSteps, engine.ts:260) по режиму и шумоподавлению заказа: [ключ, значок, «идёт», «готово»] */
function localSteps(mode: VoiceMode, noise: boolean): string[][] {
  return [
    ['load', 'upload', uiText('Загружаем запись'), uiText('Запись загружена')],
    ...(noise ? [['noise', 'denoise', uiText('Убираем шум'), uiText('Шум убран')]] : []),
    ...(mode === 'keep' ? [] : [['voice', 'wave', uiText('Меняем голос'), uiText('Голос изменён')]]),
    ['pack', 'music', uiText('Собираем запись'), uiText('Запись собрана')],
  ];
}
function engineVoice(voice: Voice): EngineVoice {
  return {
    id: voice.id,
    name: voice.name,
    orb: voiceOrb(voice) ?? '',
    tags: Array.isArray(voice.tags) ? voice.tags : [],
    mine: !!voice.is_mine,
    is_s2s: voice.is_s2s === true,
    preview_url: voice.preview_url ?? null,
  };
}
/** Голос каталога оболочки (ui.voices / ui.myVoices: server-state.ts shellVoice) в виде шаблона; is_s2s — только явное true */
function shellVoice(raw: unknown): EngineVoice | null {
  const v = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  const id = num(v?.id);
  if (!v || id === undefined) return null;
  return {
    id,
    name: typeof v.name === 'string' ? v.name : '',
    orb: typeof v.orb === 'string' ? v.orb : '',
    tags: Array.isArray(v.tags) ? v.tags.filter((tag): tag is string => typeof tag === 'string') : [],
    mine: v.mine === true || v.is_mine === true,
    is_s2s: v.is_s2s === true,
    preview_url: typeof v.preview_url === 'string' ? v.preview_url : null,
  };
}

/** Состояние одной привязки (кадра ServerStudio) */
interface Frame {
  b: EngineBinding;
  key: string;
  svc: OrderService;
  app: StudioApp;
  bridge: UploadBridge;
  driver: RunDriver;
  /** Форма, для которой прошла первая сверка (предел, режим, значения по умолчанию) */
  form: StudioForm | null;
  /** Раскладка формы на экране: режим и опубликованные поля голоса и шума */
  layout: string;
  /** id загрузки, к которой относится запись в рамке; null — выбрана только что и ещё не подтверждена */
  fileFor: string | null;
  /** Превью какой загрузки читается сейчас */
  restoring: string | null;
  /** Ошибка формы, уже сказанная в рамке */
  said: string;
  consent: ConsentSheet | null;
  /** Выбор голоса (GET голоса): новый выбор прерывает прежний */
  pick: VoicePick;
  /** Голос, который выбрали, но сервер не подтвердил переозвучку: на пилле красная подпись, создание закрыто (как шаблон) */
  badVoice: EngineVoice | null;
  voiceSeen: Voice | null;
  peek: Intent | null;
  /** id:revision:index результата, который уже открывали */
  opened: string;
  delivery: { orderId: number; core: VoiceDelivery; abort: AbortController } | null;
  audio: HTMLAudioElement | null;
  /** Пример звучания голоса */
  sample: VoiceSample;
  /** Адрес результата, волна которого посчитана (или считается) */
  wavedFor: string;
  moving: boolean;
  /** Форма была на экране при прошлой сверке: новый показ формы спрашивает остаток (как монтирование родной кнопки) */
  formShown: boolean;
  /** Идёт GET /app/balance этого кадра */
  balanceAsk: boolean;
  /** Последний номер заказа, после которого остаток в памяти считается устаревшим */
  orderSeen: number | undefined;
}

/* ---------- сервер экрана ---------- */

let instance: { bind(b: EngineBinding): EngineBound } | null = null;

export function createVoiceServer(h: VoiceEngineHooks): VoiceServer {
  const { S } = h;
  /** Состояние текущей привязки и привязка, которая сейчас активна (между release и следующим bind — null) */
  let frame: Frame | null = null;
  let bound: Frame | null = null;
  /** Кадр (состояние привязки), к которому относится S (состояние формы шаблона) и экземпляр хвоста BotRun. Именно объект, а не ключ:
      ключи кадров ServerStudio повторяются после нового входа в раздел (formSequence снова с 0), а под тем же ключом приходит
      новый сервис заказа — и, возможно, другой владелец; новый сервис начинает с пустой формы и нового хвоста, как родная форма */
  let painted: Frame | null = null;
  let tailOpts: { create: (o: Record<string, unknown>) => BotRunApi; opts: Record<string, unknown> } | null = null;
  let inst: BotRunApi | null = null;
  const facade = {} as BotRunApi;

  const cur = () => (bound && bound === frame ? frame : null);
  const live = (f: Frame) => {
    const s = f.svc.get();
    return { s, d: f.svc.derive(s) };
  };
  const onForm = () => h.mounted() && !h.tail.run;
  const audioKey = (f: Frame) => audioField(f.svc.get().form)?.key ?? 'audio';
  const limitsOf = (f: Frame): AudioLimits | null => {
    const audio = audioField(f.svc.get().form);
    if (!audio?.upload) return null;
    const maxMs = audio.upload.max_duration_ms ?? DEFAULT_MAX_MS;
    return { key: audio.key, accept: audio.upload.accept ?? [], maxMs, maxBytes: audio.upload.max_bytes ?? 20 * 1024 * 1024, note: audioLimits(audio, maxMs) };
  };
  const maxMsOf = (f: Frame | null) => (f && limitsOf(f)?.maxMs) || DEFAULT_MAX_MS;
  const hasDur = () => !!S.file && S.file.dur > 0;
  /** Подсказка-стекло у элемента; элемента нет на месте — у кнопки подвала */
  const tip = (el: Element | null, text: string) => {
    const at = el?.isConnected ? el : (h.root()?.querySelector('.ap-foot .btn') ?? null);
    if (text && at) h.ui().tip(at, uiText(text));
  };
  /** Новый файл или запись вместо заглушки превью: заглушка уходит (не выйдет — сверка прочтёт превью поля снова) */
  const fresh = (f: Frame) => {
    if (f.restoring && S.file && !(S.file.dur > 0)) {
      h.setFile(null);
      f.fileFor = null;
    }
    f.restoring = null;
  };
  /** Заявка заказа: сервиса, а пока он не проверил владельца — подсмотренная при открытии (потом её решает сервис) */
  const intentOf = (f: Frame | null): Intent | null => {
    const s = f?.svc.get();
    if (!f || !s) return null;
    return s.intent ?? (!s.identity && !s.integrityError ? f.peek : null);
  };

  /* ---- вход заказа: тело заявки (или подсмотренной), иначе форма ---- */
  /** Режим и шумоподавление заказа: показ результата сервера → тело заявки → форма */
  function sent(f: Frame | null): { mode: VoiceMode; noise: boolean; inputs: Record<string, unknown> | null } {
    const s = f?.svc.get();
    const inputs = (intentOf(f)?.body.inputs ?? null) as Record<string, unknown> | null;
    const result = voiceResult(s?.order);
    const mode = result.mode ?? MODES.find(value => value === inputs?.mode) ?? S.mode;
    let noise: boolean;
    if (result.denoise !== null) noise = result.denoise;
    else if (inputs) {
      const key = denoiseField(s?.form)?.key ?? (inputs.denoise !== undefined ? 'denoise' : 'noise');
      noise = inputs[key] !== undefined ? isOn(String(inputs[key])) : false;
    } else noise = !!S.nz[S.mode];
    return { mode, noise, inputs };
  }

  /* ---- голоса ---- */
  function voice(id: unknown): EngineVoice | null {
    const want = num(id);
    if (want === undefined) return null;
    const f = cur() ?? frame;
    if (f?.badVoice && f.badVoice.id === want) return f.badVoice;
    const own = f?.svc.get().voice;
    if (own && own.id === want) return engineVoice(own);
    const ui = h.ui();
    for (const list of [ui.voices?.() ?? [], ui.myVoices?.() ?? []])
      for (const raw of list) {
        const v = shellVoice(raw);
        if (v && v.id === want) return v;
      }
    return null;
  }
  function stopSample() {
    frame?.sample.stop();
  }

  /* ---- готовность ---- */
  function missing(): Missing | null {
    const f = cur();
    if (!f) return 'load';
    const { s, d } = live(f);
    if (s.intent && !d.orderId) return '';
    if (!s.identity && !s.integrityError) return 'load';
    if (s.integrityError || !s.identity || s.identity.appId !== f.app.id) return 'blocked';
    /* форма не загрузилась или устарела: кнопка «Обновить форму» (recovery) */
    if (!s.form) return s.error ? '' : 'load';
    if (s.versionStale) return '';
    if (!audioField(s.form)?.upload) return 'off';
    if (!s.form.available) return 'unavailable';
    return null;
  }
  function ready(): Ready {
    const f = cur();
    if (!f) return 'upload';
    const { s } = live(f);
    /* неподтверждённая загрузка: «Продолжить загрузку» (recovery) — кнопка активна; иначе — загрузка идёт */
    if (f.bridge.pending()) return s.error === TRANSPORT && !s.busy ? '' : 'upload';
    /* запись в рамке без загрузки поля (загрузку сняли, пока кадр был закрыт) — загрузить заново */
    if (!s.inputs[audioKey(f)]) return 'upfail';
    /* цена «за секунду»: в заказ уходит расчёт сервера под длину записи; до него у формы 0 — заказ получил бы 409 price_changed */
    if (s.form?.chargeable && studioUnitPrice(s.form, 'second') !== null && !f.svc.derive(s).quoted) return 'price';
    return '';
  }
  /** Что починить вместо создания: форма не загрузилась или устарела — перечитать; загрузка не подтверждена — продолжить тем же id */
  function recovery(f: Frame): ((btn: HTMLElement) => void) | null {
    const { s } = live(f);
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return () => f.svc.setters.revision(value => value + 1);
    if (f.bridge.pending() && !s.busy && s.error === TRANSPORT) return btn => void busyButton(btn, () => f.bridge.resume());
    return null;
  }
  function label(): string | null {
    const f = cur();
    const act = uiText(actLabel(S.mode, !!S.nz[S.mode]));
    if (!f) return perMinute(frame?.app) === null ? act : null;
    const { s, d } = live(f);
    if (s.intent && !d.orderId) return uiText('Продолжить проверку');
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return uiText('Обновить форму');
    if (f.bridge.pending() && !s.busy && s.error === TRANSPORT) return uiText('Продолжить загрузку');
    const known = perMinute(f.app, s.form) !== null || (!!s.form && Number.isFinite(s.form.expected_price));
    if (s.form?.chargeable === false || !known) return act;
    return null;
  }

  /* ---- сверка формы с сервисом (каждый зафиксированный вид) ---- */
  const layoutOf = () => `${S.mode}|${server.has('voice')}|${server.has('denoise')}`;
  function placeholder(): EngineFile {
    return { name: uiText('Запись'), size: 0, url: '', dur: 0, kind: 'audio', peaks: flatWave() };
  }
  function restore(f: Frame, id: string) {
    if (!limitsOf(f) || !f.svc.get().identity) return;
    f.restoring = id;
    f.fileFor = id;
    /* пока читается превью — заглушка без длины: подвал «Читаем запись», создание закрыто */
    h.setFile(placeholder());
    h.paintSrc();
    void f.bridge.restore(id).finally(() => {
      if (f.restoring === id) f.restoring = null;
      if (cur() === f && h.mounted()) formSync(f);
    });
  }
  function formSync(f: Frame) {
    const { s, d } = live(f);
    const form = s.form;
    if (form && f.form !== form) {
      /* первая сверка этой формы: предел, размер рамки, режим */
      f.form = form;
      const limits = limitsOf(f);
      h.setMax(maxMsOf(f) / 1000);
      if (limits) h.up.o.mb = limits.maxBytes / 1048576;
      S.mode = voiceMode(form, f.b.initialInputs);
    }
    /* режим и «Убрать шум» по умолчанию — только в опубликованные поля (VoiceOrderForm.tsx:94-102) */
    if (form && !d.locked) {
      const mField = modeField(form),
        nField = denoiseField(form);
      if (mField || nField)
        f.svc.setters.inputs(previous => {
          let next = previous;
          if (mField && previous[mField.key] !== S.mode) next = { ...next, [mField.key]: S.mode };
          if (nField && previous[nField.key] === undefined) next = { ...next, [nField.key]: denoiseDefault(nField, S.mode, f.b.initialInputs) };
          return next;
        });
    }
    const now = f.svc.get();
    if (form) {
      const nField = denoiseField(form);
      const nz = nField ? isOn(now.inputs[nField.key]) : false;
      if (S.nz[S.mode] !== nz) {
        S.nz[S.mode] = nz;
        const sw = h.root()?.querySelector('[data-vc-noise]');
        if (sw && onForm()) core().switchSet(sw, nz);
      }
      const vField = voiceField(form);
      const picked = f.badVoice?.id ?? (vField ? (num(now.inputs[vField.key]) ?? null) : null);
      if (String(S.voice) !== String(picked) || f.voiceSeen !== now.voice) {
        S.voice = picked;
        f.voiceSeen = now.voice;
        if (onForm()) h.paintVp();
      }
    }
    const layout = layoutOf();
    if (layout !== f.layout) {
      f.layout = layout;
      if (onForm()) h.paintPage();
    }
    if (!onForm()) return;
    /* запись в рамке — та, что у поля формы */
    const key = audioKey(f),
      id = now.inputs[key];
    if (S.file && f.fileFor && f.fileFor !== id && f.restoring !== f.fileFor) {
      h.setFile(null);
      f.fileFor = null;
    }
    if (S.file && f.fileFor === null && id && !f.bridge.pending()) f.fileFor = id;
    if (!S.file && id && !f.restoring) restore(f, id);
    /* ошибка формы (не загрузилась, файлы повтора недоступны) — один раз в рамке */
    const error = now.integrityError ? '' : now.error;
    if (error !== f.said) {
      f.said = error;
      if (FORM_SAY.includes(error)) h.up.say(uiText(error));
    }
    h.paintSrc();
  }
  function changed(f: Frame) {
    if (cur() !== f || !h.mounted()) return;
    formSync(f);
    f.driver.sync();
    f.consent?.refresh();
    balance(f);
  }
  /** Остаток для «Не хватает Circle» — тот же, что у родной кнопки (useCircleBalance, GET /app/balance, память на минуту): спрашивается
      при каждом показе формы, если в памяти его нет; созданный или открытый заказ делает память устаревшей (сервер списал Circle).
      Пришёл — подвал пересчитывается. Остаток GET /app/me сервиса здесь не берётся: он с открытия кадра и не обновляется */
  function balance(f: Frame) {
    const orderId = f.svc.derive().orderId;
    if (orderId && orderId !== f.orderSeen) {
      f.orderSeen = orderId;
      forgetCircleBalance();
    }
    const shown = onForm(),
      fresh = shown && !f.formShown;
    f.formShown = shown;
    if (!fresh || f.balanceAsk || circleBalance() !== null || f.svc.get().form?.chargeable === false) return;
    f.balanceAsk = true;
    const seen = f.orderSeen;
    void endpoints
      .balance()
      .then(
        value => {
          if (f.orderSeen === seen) rememberCircleBalance(value?.circle);
        },
        () => {
          /* ошибка — проверки нет, решает сервер (402), как у родной кнопки */
        },
      )
      .finally(() => {
        f.balanceAsk = false;
        if (cur() === f && onForm()) h.sync();
      });
  }

  /* ---- итог ---- */
  const audioAsset = (f: Frame) => {
    const order = f.svc.get().order;
    return order?.status === 'done' && order.result?.available ? order.result.assets.find(item => item.mime.startsWith('audio/')) : undefined;
  };
  /** Волна и длина итога: из ответа сервера, иначе из самого файла (WebAudio); карточка плеера меняется на месте */
  function resultWave(f: Frame) {
    const r = h.tail.run,
      s = f.svc.get();
    if (!r || r.st !== 'done') return;
    const snap = r.snap as VoiceSnap;
    const server = voiceResult(s.order);
    const apply = (peaks: number[] | null, ms: number | null) => {
      if (h.tail.run !== r) return;
      let repaint = false;
      if (peaks && peaks.length && peaks.join() !== snap.peaks.join()) {
        snap.peaks = peaks;
        const wave = h.root()?.querySelector('#vcRw');
        if (wave) wave.innerHTML = core().wave.html(peaks);
        repaint = true;
      }
      if (ms && ms > 0 && snap.dur !== Math.max(1, Math.round(ms / 1000))) {
        snap.dur = Math.max(1, Math.round(ms / 1000));
        repaint = true;
      }
      if (repaint) h.rpPaint(true);
    };
    apply(server.peaks, server.durationMs);
    const url = s.result?.mime.startsWith('audio/') ? s.result.url : '';
    if (!url || f.wavedFor === url || (server.peaks && server.durationMs)) return;
    f.wavedFor = url;
    void fetch(url)
      .then(response => response.blob())
      .then(blob => filePeaks(blob))
      .then(wave => {
        if (wave && f.svc.get().result?.url === url) apply(server.peaks ?? wave.peaks, server.durationMs ?? wave.durationMs);
      })
      .catch(() => {});
  }
  /** Заказ готов (каждая сверка): результат открывается один раз на id:revision:index, отправка — сведения заказа, отметки отправки — на кнопки */
  function onDone(f: Frame) {
    const s = f.svc.get(),
      order = s.order;
    if (!order || order.status !== 'done') return;
    const asset = audioAsset(f);
    const key = asset ? `${order.id}:${order.result_revision}:${asset.index}` : '';
    if (key && !s.result && !s.busy && f.opened !== key) {
      f.opened = key;
      void f.svc.actions.openResult(asset!.index).then(() => {
        const now = f.svc.get();
        if (cur() === f && !now.result && now.error) tip(h.root()?.querySelector('#vcRpb') ?? null, now.error);
      });
    }
    if (order.result?.available && f.delivery?.orderId !== order.id) {
      f.delivery?.abort.abort();
      const abort = new AbortController();
      f.delivery = { orderId: order.id, core: createVoiceDelivery(order.id), abort };
      void f.delivery.core.load(abort.signal);
    }
    const r = h.tail.run;
    if (r && r.st === 'done' && f.delivery?.orderId === order.id) {
      const sentFlags = (r.sent ??= {});
      for (const k of ['vm', 'file'] as const) {
        if (sentFlags[k] || !f.delivery.core.sent(k === 'vm' ? 'voice' : 'file')) continue;
        sentFlags[k] = true;
        const main = h.mainOf(r) === k;
        const btn = h.root()?.querySelector<HTMLElement>(main ? '[data-vc-main]' : '[data-vc-alt]');
        if (!btn) continue;
        const [icon, text] = h.sendView(r, k, main);
        btn.innerHTML = h.ic(icon) + '<span>' + h.esc(text) + '</span>';
        btn.classList.toggle('sent', !main);
      }
    }
    resultWave(f);
  }
  function stopResult(f: Frame | null) {
    if (!f?.audio) return;
    f.audio.onended = null;
    f.audio.pause();
    f.audio = null;
  }

  /* ---- хвост BotRun: фасад одного экземпляра на ключ кадра ---- */
  function rebuild() {
    if (!tailOpts) return;
    const { create, opts } = tailOpts;
    const base = typeof opts.missing === 'function' ? (opts.missing as () => string) : () => '';
    inst = create({
      ...opts,
      drive: (r: BotRunRun) => server.drive(r),
      failText: (r: BotRunRun) => server.failText(r),
      receipt: (r: BotRunRun) => server.receipt(r),
      foot: serverFoot((opts.foot as Record<string, unknown>) ?? {}, 'vc'),
      steps: () => server.steps(),
      snap: () => server.snap(),
      price: () => server.sentPrice(),
      /* повтор заявки и «Попробовать ещё раз» решает сервис: форма в памяти (запись, голос) им не нужна */
      missing: () => {
        const f = cur();
        if (f) {
          const { s, d } = live(f);
          if (s.intent || d.orderId) return '';
        }
        return base();
      },
    });
    for (const key of Object.keys(inst)) {
      if (key === 'run' || Object.hasOwn(facade, key)) continue;
      (facade as unknown as Record<string, unknown>)[key] = (...args: unknown[]) =>
        ((inst as unknown as Record<string, (...a: unknown[]) => unknown>)[key])(...args);
    }
  }
  Object.defineProperty(facade, 'run', { enumerable: true, get: () => inst?.run ?? null });

  /* ---- привязка кадра ---- */
  function bind(b: EngineBinding): EngineBound {
    if (!frame || frame.key !== b.key || frame.svc !== b.svc) {
      if (frame) {
        frame.bridge.cancel();
        frame.consent?.close();
        frame.pick.abort();
        frame.delivery?.abort.abort();
        stopResult(frame);
        stopSample();
      }
      frame = makeFrame(b);
    } else frame.b = b;
    const mine = frame;
    bound = mine;
    return {
      changed: () => changed(mine),
      release() {
        if (bound === mine) bound = null;
      },
    };
  }
  function makeFrame(b: EngineBinding): Frame {
    const f = {} as Frame;
    const guard =
      <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (frame === f) fn(...args);
      };
    Object.assign(f, {
      b,
      key: b.key,
      svc: b.svc,
      app: b.app,
      form: null,
      layout: '',
      fileFor: null,
      restoring: null,
      said: '',
      consent: null,
      pick: createVoicePick(),
      badVoice: null,
      voiceSeen: null,
      peek: null,
      opened: '',
      delivery: null,
      audio: null,
      sample: createVoiceSample(),
      wavedFor: '',
      moving: false,
      formShown: false,
      balanceAsk: false,
      orderSeen: undefined,
    });
    f.bridge = createUploadBridge({
      svc: b.svc,
      limits: () => limitsOf(f),
      owner: () => f.svc.get().identity?.owner,
      consent: guard((file: File, recorded: boolean) => {
        f.consent = openConsentSheet({
          rights: () => f.svc.get().rights,
          locked: () => f.svc.derive().locked,
          setRights: on => f.svc.setters.rights(on),
          onContinue: () => {
            f.consent = null;
            fresh(f);
            void f.bridge.pick(file, recorded);
          },
          /* лист закрыли — файл не грузится */
          onClose: () => {
            f.consent = null;
          },
        });
      }),
      say: guard((text: string) => h.up.say(text)),
      put: guard((file: EngineFile) => {
        f.fileFor = f.restoring;
        f.restoring = null;
        if (file.rec) file.name = uiFormat('Запись {0}', ++h.REC.n);
        h.put(file);
      }),
      drop: guard((file: EngineFile | null) => {
        if (file && S.file !== file) return;
        h.setFile(null);
        f.fileFor = null;
        h.paintSrc();
      }),
      update: guard(() => h.paintSrc()),
    });
    f.driver = createRunDriver({
      svc: b.svc,
      tail: () => h.tail,
      root: () => h.root(),
      mounted: () => cur() === f && h.mounted(),
      localSteps: () => server.steps(),
      paintForm: () => h.paintPage(),
      tip: (el, text) => tip(el, text),
      onDone: () => onDone(f),
    });
    return f;
  }

  const server: VoiceServer = {
    ui: base =>
      serverUi(base, {
        poorBalance: () => {
          const f = cur();
          /* кнопка чинит («Обновить форму», «Продолжить загрузку»), а не создаёт: «Не хватает · Пополнить» её не заслоняет */
          if (!f || recovery(f)) return Infinity;
          const s = f.svc.get();
          return poorBalance({ balance: circleBalance(), form: s.form, intent: s.intent }, hasDur());
        },
      }),
    entry() {
      const f = cur();
      const init = f?.b.initialInputs ?? {};
      const form = f?.svc.get().form;
      const asked = MODES.find(value => value === String(init.mode ?? ''));
      const mode = form ? voiceMode(form, init) : (asked ?? S.mode);
      const noise = init.noise ?? init.denoise;
      return {
        mode,
        out: voiceOut(mode, init) === 'voice' ? 'vm' : 'file',
        ...(noise !== undefined && noise !== null && noise !== '' ? { noise: isOn(String(noise)) } : {}),
      };
    },
    open(o) {
      const f = cur();
      if (!f) {
        /* без привязки (кадр не активен) — форма без действий: missing() держит «Проверяем доступность» */
        h.setup(o);
        h.paintPage();
        return;
      }
      if (painted !== f) {
        /* другой кадр или новый сервис под прежним ключом: запись, голос, шум, скидка и хвост прежнего сюда не переходят */
        h.liveStop();
        h.setFile(null);
        S.voice = null;
        S.nz = { keep: true, other: false };
        S.cheap = false;
        S.playing = null;
        rebuild();
        painted = f;
        f.layout = '';
        f.form = null;
      }
      /* открытие на форме — новый показ кнопки: остаток спрашивается, если его нет в памяти */
      f.formShown = false;
      const root = h.root();
      if (h.tail.run && root) {
        h.tail.show(root);
        changed(f);
        return;
      }
      h.setup(o);
      f.peek = peekIntent(f.app.id);
      if (f.driver.first(f.peek) === 'form') {
        f.layout = layoutOf();
        if (root) root.scrollTop = 0;
      }
      changed(f);
    },
    close() {
      stopSample();
      const f = frame;
      if (!f) return;
      f.consent?.close();
      f.consent = null;
      f.pick.abort();
      stopResult(f);
    },
    tail(create, opts) {
      tailOpts = { create, opts };
      rebuild();
      return facade;
    },
    wrap<T>(slot: 'cheap' | 'vsub', value: T, extra?: unknown): T {
      if (slot === 'cheap') {
        const cheap = value as unknown as { price?: unknown; go?: unknown; where?: unknown };
        const offer = () => {
          const f = cur();
          return f ? studioCheapOffer(f.svc.get().form) : null;
        };
        /* скидку «Сделать дешевле» публикует форма (Q5): без неё у подвала нет ни цены, ни действия скидки;
           процент в листе «Не хватает Circle» — её percent (без него лист берёт свои 10 %, BotCore.CHEAP) */
        return {
          where: cheap.where,
          get price() {
            return offer() ? cheap.price : undefined;
          },
          get go() {
            return offer() ? cheap.go : undefined;
          },
          get percent() {
            return offer()?.percent;
          },
        } as unknown as T;
      }
      const v = extra as EngineVoice | null;
      if (!v || v.mine) return value;
      /* «ещё N+ голосов» — только с числом каталога сервера (voiceSub, voice-model.ts:224); строка шаблона не в t() — здесь словарём профиля */
      const total = server.voiceTotal();
      return (total > 0 ? uiFormat(' · ещё {0}+ голосов', total.toLocaleString('ru-RU')) : '') as T;
    },
    has(field) {
      const form = cur()?.svc.get().form;
      if (!form) return true;
      return field === 'denoise' ? !!denoiseField(form) : !!voiceField(form);
    },
    missing,
    ready,
    missText(kind) {
      const f = cur();
      const s = f?.svc.get();
      switch (kind) {
        case 'load':
          return uiText('Проверяем доступность');
        case 'blocked':
          return uiText(s?.integrityError || 'Проверяем доступность');
        case 'off':
          return uiText(s?.error || 'Этот инструмент сейчас недоступен. Попробуйте обновить форму.');
        case 'unavailable':
          return uiText('Создание сейчас недоступно');
        case 'upload':
          return uiText('Загружаем запись');
        case 'upfail':
          return uiText('Загрузите запись заново');
        case 'price':
          return uiText('Цена этого выбора пока не подтверждена');
        default:
          return '';
      }
    },
    label,
    perMinute: () => perMinute(frame?.app, (cur() ?? frame)?.svc.get().form) ?? 0,
    price(cheap, durSec) {
      const f = cur();
      const form = f?.svc.get().form ?? null;
      if (cheap) {
        const offer = studioCheapOffer(form);
        if (offer) return offer.price;
      }
      /* расчёт сервера под нынешнюю запись — цена кнопки; до него — оценка по цене секунды */
      const d = f?.svc.derive();
      if (form?.chargeable && d?.quoted) return d.price;
      return voicePrice(f?.app ?? frame?.app, form, durSec && durSec > 0 ? durSec * 1000 : null).price ?? 0;
    },
    unit() {
      const minute = perMinute(frame?.app, (cur() ?? frame)?.svc.get().form);
      return minute ? core().html.unit(minute, uiText('в минуту')) : '';
    },
    note() {
      const f = cur();
      return audioLimits(f ? audioField(f.svc.get().form) : undefined, maxMsOf(f));
    },
    upOpts: () => ({
      accept: ACCEPT,
      /* свои строки рамки BotCore.upload («Загрузить», «Записать», «Заменить», «Убрать», отказы) — словарём профиля */
      tx: (template: string, ...values: unknown[]) => uiFormat(template, ...values),
      onPick(files) {
        const f = cur();
        if (!f || !files[0]) return;
        fresh(f);
        void f.bridge.pick(files[0]);
      },
    }),
    voice,
    /* число каталога сервера (catalog_total сессии и страниц каталога оболочки, bridge/catalog-total); не прислал — 0, подписи нет */
    voiceTotal: () => catalogTotal(),
    pickVoice(id) {
      const f = cur();
      if (!f || !h.mounted() || h.tail.run || id == null) return;
      const field = voiceField(f.svc.get().form);
      if (!field || f.svc.derive().locked) return;
      const pill = () => h.root()?.querySelector('#vcVp') ?? null;
      /* сведения голоса (voice-pick): id сверен, прежний выбор прерван; устаревший ответ — null */
      f.pick
        .pick(id, { s2s: true })
        .then(picked => {
          if (!picked || cur() !== f) return;
          const details = picked.voice;
          stopSample();
          S.playing = null;
          if (!picked.ok) {
            /* как шаблон: голос встаёт с красной подписью, создание закрыто; в заказ он не попадает */
            f.badVoice = { ...engineVoice(details), is_s2s: false };
            S.voice = details.id;
            h.paintVp();
            h.sync();
            return;
          }
          if (f.svc.derive().locked) return;
          f.badVoice = null;
          f.svc.setters.voice(details);
          f.svc.setters.inputs(previous => ({ ...previous, [field.key]: String(details.id) }));
          S.voice = details.id;
          h.paintVp();
          h.sync();
          h.ui().buzz();
        })
        .catch(() => {
          tip(pill(), 'Не удалось выбрать голос. Попробуйте ещё раз.');
        });
    },
    sample(id) {
      const f = cur();
      if (!f) return;
      const same = String(S.playing) === String(id);
      stopSample();
      S.playing = null;
      /* пример звучания (voice-pick): тот же голос — только стоп; кончился или не пошёл — подпись пилла на месте */
      const started =
        !same &&
        f.sample.play(voice(id)?.preview_url, () => {
          S.playing = null;
          if (onForm()) h.paintVp();
        });
      if (started) {
        S.playing = id;
        h.ui().buzz();
      }
      h.paintVp();
    },
    stopSample,
    noise(on) {
      const f = cur();
      const field = f ? denoiseField(f.svc.get().form) : undefined;
      if (!f || !field || f.svc.derive().locked) {
        /* без поля или пока форма заперта — переключатель не двигается */
        S.nz[S.mode] = !on;
        return;
      }
      f.svc.setters.inputs(previous => ({ ...previous, [field.key]: onValue(field, on) }));
    },
    remove() {
      const f = cur();
      if (!f) return;
      f.bridge.cancel();
      f.restoring = null;
      f.fileFor = null;
      f.svc.actions.clearUpload(audioKey(f));
    },
    canRecord: () => !!(window.SteosUI as unknown as { canRecord?(): boolean }).canRecord?.(),
    micText: kind => uiText(kind === 'off' ? 'Запись здесь недоступна, загрузите файл' : 'Не удалось включить микрофон. Разрешите доступ или загрузите файл.'),
    recorded({ blob, url }) {
      if (url)
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* адрес записи больше не нужен: мост делает свой WAV */
        }
      /* плитка записи уходит из рамки (REC.st уже idle) */
      h.paintSrc();
      const f = cur();
      if (!f) return;
      if (!blob) {
        h.up.say(uiText('Не удалось прочитать запись, попробуйте ещё раз'));
        return;
      }
      fresh(f);
      void f.bridge.recorded(blob, maxMsOf(f));
    },
    start(btn) {
      const f = cur();
      if (!f) return;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) {
        h.tail.start();
        return;
      }
      const fix = recovery(f);
      if (fix) {
        fix(btn);
        return;
      }
      if (h.foot.poor()) {
        h.foot.poorOpen();
        return;
      }
      h.tail.start();
    },
    steps() {
      const { mode, noise } = sent(cur());
      return localSteps(mode, noise);
    },
    snap() {
      const f = cur();
      const s = f?.svc.get();
      const it = intentOf(f);
      const { mode, noise, inputs } = sent(f);
      const keep = mode === 'keep';
      const result = voiceResult(s?.order);
      const voiceId = keep ? undefined : inputs ? num(inputs.voice_id) : num(S.voice);
      const v = voiceId !== undefined ? voice(voiceId) : null;
      /* запись в памяти — только если она и есть запись заказа */
      const audioId = inputs && f ? inputs[audioKey(f)] : undefined;
      const own = S.file && S.file.dur > 0 && (!inputs || (f && f.fileFor !== null && f.fileFor === audioId)) ? S.file : null;
      const ms = result.durationMs ?? (own ? own.dur * 1000 : null);
      const nField = denoiseField(s?.form);
      return {
        file: own ? { ...own } : null,
        dur: ms ? Math.max(1, Math.round(ms / 1000)) : null,
        mode,
        voice: voiceId,
        vname: keep ? uiText('Ваш голос') : result.voiceName || v?.name || '',
        orb: keep ? '' : v?.orb || '',
        noise,
        out: voiceOut(mode, f?.b.initialInputs, mode === 'other' || (!!s?.form && !nField) || noise) === 'voice' ? 'vm' : 'file',
        cheap: !!(it ? it.body.cheap : S.cheap),
        peaks: result.peaks ?? (own?.peaks.length ? own.peaks.slice() : flatWave()),
      };
    },
    sentPrice: () => cur()?.svc.derive().price ?? 0,
    drive(r) {
      const f = cur();
      if (f) void f.driver.drive(r);
    },
    failText(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const order = s?.order ?? null;
      const snap = r.snap as Partial<VoiceSnap>;
      const texts = voiceTexts(snap.mode === 'keep' ? 'keep' : 'other', !!snap.noise);
      const steps = tailSteps(order);
      return failText(r, {
        order,
        error: s ? s.integrityError || s.error : '',
        texts: { modH: texts.modH, techH: texts.techH, notMade: texts.notMade },
        step: steps.length ? steps[tailCurrent(order, steps)] : undefined,
      });
    },
    receipt(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const cheap = s?.intent ? !!s.intent.body.cheap : !!r.snap.cheap;
      return receipt(r, s?.order ?? null, { cheap, where: 'в аудио' });
    },
    click(d, el) {
      const f = cur();
      if (d.vcRetry === undefined && d.vcRchk === undefined && d.vcFix === undefined) return false;
      if (!f || f.driver.busy()) return true;
      if (d.vcRetry !== undefined) h.tail.retry();
      else if (d.vcRchk !== undefined) void f.driver.check(el);
      else void f.driver.leave(() => h.tail.exit(() => h.paintPage()), false, audioKey(f));
      return true;
    },
    leave(go, replace) {
      const f = cur();
      if (!f) return;
      void f.driver.leave(
        () => {
          if (replace) {
            f.bridge.cancel();
            f.fileFor = null;
            f.restoring = null;
          }
          go();
        },
        replace,
        audioKey(f),
      );
    },
    play(r, at) {
      const f = cur();
      if (!f || r.st !== 'done') return true;
      const result = f.svc.get().result;
      const { RP } = h;
      if (!result || !result.mime.startsWith('audio/')) {
        tip(h.root()?.querySelector('#vcRpb') ?? null, 'Запись ещё загружается');
        return true;
      }
      let audio = f.audio;
      if (!audio || audio.src !== result.url) {
        stopResult(f);
        audio = new Audio(result.url);
        f.audio = audio;
        const playing = audio;
        audio.onended = () => {
          if (f.audio !== playing) return;
          RP.on = false;
          RP.pos = 0;
          cancelAnimationFrame(RP.raf);
          h.rpPaint(true);
        };
      }
      if (RP.on) {
        audio.pause();
        RP.on = false;
        RP.pos = audio.currentTime || RP.pos;
        cancelAnimationFrame(RP.raf);
        h.rpPaint(true);
        return true;
      }
      const length = (r.snap as VoiceSnap).dur || audio.duration || 1;
      if (at != null) RP.pos = length * at;
      if (RP.pos >= length) RP.pos = 0;
      try {
        audio.currentTime = RP.pos;
      } catch {
        /* звук ещё не готов к перемотке: начнётся с начала */
      }
      RP.on = true;
      h.ui().buzz();
      h.rpPaint(true);
      const current = audio;
      current.play().catch(() => {
        if (f.audio !== current) return;
        RP.on = false;
        h.rpPaint(true);
      });
      const tick = () => {
        if (!RP.on || f.audio !== current) return;
        RP.pos = current.currentTime;
        h.rpPaint(false);
        RP.raf = requestAnimationFrame(tick);
      };
      RP.raf = requestAnimationFrame(tick);
      return true;
    },
    audioStop(rewind) {
      const f = frame;
      cancelAnimationFrame(h.RP.raf);
      if (!f?.audio) return;
      f.audio.pause();
      if (rewind)
        try {
          f.audio.currentTime = 0;
        } catch {
          /* ничего не перематывается: звук не загружен */
        }
    },
    async download() {
      const f = cur();
      if (!f) throw new Error('not_bound');
      let result = f.svc.get().result;
      if (!result) {
        const asset = audioAsset(f);
        if (!asset) throw new Error('result_unavailable');
        await f.svc.actions.openResult(asset.index);
        result = f.svc.get().result;
      }
      if (!result) throw new Error('result_unavailable');
      const data = await (await fetch(result.url)).blob();
      return { data, name: result.name };
    },
    async send(btn, k) {
      const f = cur();
      const order = f?.svc.get().order;
      const delivery = f && order?.status === 'done' && f.delivery?.orderId === order.id ? f.delivery.core : null;
      const out = k === 'vm' ? 'voice' : 'file';
      if (!f || h.tail.run?.st !== 'done' || !delivery || !delivery.ready()) {
        tip(btn, 'Проверяем отправку');
        throw new Error('delivery_not_ready');
      }
      if (!delivery.can(out)) {
        tip(btn, 'Этот способ отправки сейчас недоступен');
        throw new Error('delivery_unavailable');
      }
      const ok = await busyButton(btn, () => delivery.send(out));
      if (!ok) {
        if (btn.isConnected) tip(btn, delivery.error() || 'Отправка пока не подтверждена. Нажмите ещё раз — проверим тот же запрос.');
        throw new Error('delivery_unconfirmed');
      }
    },
    openChat(btn, k, main) {
      const f = cur();
      markSeen(f?.svc.derive().orderId);
      if (main && platform.kind === 'telegram') {
        platform.close();
        return true;
      }
      /* подсказки шаблона (engine.ts send: BotCore.TIPS.chat(noun) и OUT.done + «, в отдельной теме») — целыми фразами словаря профиля
         (склеенное «прислал » + noun не переводится по частям) */
      const vm = k === 'vm';
      tip(
        btn,
        main
          ? vm
            ? 'Тут откроется тема чата, куда бот прислал голосовое'
            : 'Тут откроется тема чата, куда бот прислал запись'
          : `${sentLabel(vm ? 'voice' : 'file')}, в отдельной теме`,
      );
      return true;
    },
    transfer(btn, use) {
      const f = cur();
      const s = f?.svc.get();
      const order = s?.order;
      if (!f || !s || !order || order.status !== 'done' || !audioAsset(f) || f.moving) return;
      f.moving = true;
      markSeen(order.id);
      void busyButton(btn, () => transferVoiceResult({ ownerId: s.identity?.owner, order, use }))
        .catch(() => {
          if (btn.isConnected) tip(btn, 'Не удалось передать запись. Откройте работу в «Истории» и попробуйте снова.');
        })
        .finally(() => {
          f.moving = false;
        });
    },
  };
  /* ---- смена языка профиля (i18n: событие steos:locale) ---- */
  /** Подписи этапов идущего заказа и «Ваш голос» снимка — строки на момент создания хвоста: им — подписи нового языка (ключи этапов те же) */
  function relabel(r: BotRunRun) {
    const fresh = server.steps();
    for (const step of r.steps) {
      const same = fresh.find(item => item[0] === step[0]);
      if (same) {
        step[2] = same[2];
        step[3] = same[3];
      }
    }
    const snap = r.snap as Partial<VoiceSnap>;
    if (snap.mode === 'keep') snap.vname = uiText('Ваш голос');
  }
  /** Открытый экран рисуется заново на новом языке: хвост — TAIL.show (ход итога — на месте), форма — paintPage; затем обычная сверка */
  function relocale() {
    const r = h.tail.run;
    if (r) relabel(r);
    const f = cur();
    const root = h.root();
    if (!f || !root || !h.mounted()) return;
    if (r) {
      h.tail.show(root);
      h.rpPaint(true);
    } else h.paintPage();
    changed(f);
  }
  document.addEventListener('steos:locale', relocale);
  /* новый снимок Me (пополнение, слияние) уже положил остаток в память кнопки (tool-helpers): подвал формы — по нему */
  document.addEventListener('steos:account-updated', () => {
    if (cur() && onForm()) h.sync();
  });

  instance = { bind };
  return server;
}

/** ENGINES.voice.bind: привязка кадра к серверу экрана, созданному движком (без движка в серверном режиме — пустая привязка) */
export function bindVoiceEngine(b: EngineBinding): EngineBound {
  return instance ? instance.bind(b) : { changed() {}, release() {} };
}

/** Экран «voice» реестра движков студии: движок шаблона window.BotVoice, атрибуты data-vc-*, правая панель, знак заказа «voice» */
export const VOICE_ENGINE: EngineScreen = { global: 'BotVoice', prefix: 'vc', side: true, kind: 'voice', bind: bindVoiceEngine };

export function registerVoiceEngine() {
  ENGINES.voice = VOICE_ENGINE;
}
